import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, ChevronDown, Edit3, Check, Sparkles, Loader2, Ruler } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { fmtNum } from '@/utils/format';
import { analyzeText } from '@/lib/ai';
import type { MealComponent } from '@/types';

// ════════════════════════════════════════════════════════════════
// ComponentsEditor v3 — Аккордеон с inline-редактированием
//
// КЛЮЧЕВЫЕ ИЗМЕНЕНИЯ v3:
// 1. Компактный аккордеон: заголовок "Состав блюда (N)" → раскрытие
//    → список строк (НЕ карточек). Редактирование inline в строке.
// 2. Свободная форма веса: можно ввести число ИЛИ описание
//    ("кусочек помидора", "горсть орехов", "со спичечный коробок").
//    AI кнопка отправляет полную фразу и сам интерпретирует.
// ════════════════════════════════════════════════════════════════

interface Props {
  components: MealComponent[];
  onChange: (newComponents: MealComponent[], recalcTotals: {
    weight_g: number;
    calories: number;
    protein_g: number;
    fat_g: number;
    carbs_g: number;
  }) => void;
}

// Считает итоги блюда из массива компонентов
function calcTotals(components: MealComponent[]) {
  return components.reduce(
    (acc, c) => ({
      weight_g: acc.weight_g + (Number(c.weight_g) || 0),
      calories: acc.calories + (Number(c.calories) || 0),
      protein_g: acc.protein_g + (Number((c as any).protein_g) || 0),
      fat_g: acc.fat_g + (Number((c as any).fat_g) || 0),
      carbs_g: acc.carbs_g + (Number((c as any).carbs_g) || 0),
    }),
    { weight_g: 0, calories: 0, protein_g: 0, fat_g: 0, carbs_g: 0 }
  );
}

// 🆕 v3: расширенный тип с описательным весом
interface MealComponentExt extends MealComponent {
  weight_desc?: string;  // например "кусочек помидора"
  protein_g?: number;
  fat_g?: number;
  carbs_g?: number;
}

function emptyComponent(): MealComponentExt {
  return {
    name: '',
    weight_g: 0,
    calories: 0,
    protein_g: 0,
    fat_g: 0,
    carbs_g: 0,
  };
}

export function ComponentsEditor({ components, onChange }: Props) {
  const { haptic } = useTelegram();
  const [expanded, setExpanded] = useState(true);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  function emitChange(newComponents: MealComponent[]) {
    const totals = calcTotals(newComponents);
    onChange(newComponents, {
      weight_g: Math.round(totals.weight_g),
      calories: Math.round(totals.calories),
      protein_g: +totals.protein_g.toFixed(1),
      fat_g: +totals.fat_g.toFixed(1),
      carbs_g: +totals.carbs_g.toFixed(1),
    });
  }

  function updateComponent(idx: number, patch: Partial<MealComponent>) {
    const next = components.map((c, i) =>
      i === idx ? { ...c, ...patch } : c
    );
    emitChange(next);
  }

  function removeComponent(idx: number) {
    haptic('light');
    const next = components.filter((_, i) => i !== idx);
    emitChange(next);
    if (editingIdx === idx) setEditingIdx(null);
  }

  function addComponent() {
    haptic('light');
    const next = [...components, emptyComponent()];
    emitChange(next);
    setExpanded(true);
    setEditingIdx(next.length - 1);
  }

  const hasComponents = components.length > 0;
  const totals = calcTotals(components);

  return (
    <div className="mt-3 pt-3 border-t border-line/30">
      {/* Заголовок аккордеона — компактный, с итогами */}
      <button
        type="button"
        onClick={() => {
          setExpanded(!expanded);
          haptic('light');
        }}
        className="w-full flex items-center justify-between hover:bg-bg-elevated/40 -mx-1 px-1 py-1 rounded transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronDown
            size={14}
            className={`text-sky-glow shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
          <span className="text-[11px] text-sky-glow font-medium uppercase tracking-wider">
            Состав блюда
          </span>
          {hasComponents && (
            <span className="text-[10px] text-sky-glow/80 bg-sky-glow/10 px-1.5 py-0.5 rounded-full shrink-0">
              {components.length}
            </span>
          )}
        </div>
        {hasComponents && (
          <span className="text-[10px] text-ink-dim shrink-0">
            {Math.round(totals.weight_g)}г · {Math.round(totals.calories)} ккал
          </span>
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 space-y-0.5">
              {!hasComponents && (
                <p className="text-[11px] text-ink-dim italic text-center py-2">
                  Добавь компоненты — итог блюда пересчитается автоматически
                </p>
              )}

              {/* Компактные строки — аккордеон внутри */}
              {components.map((c, idx) => {
                const isEditing = editingIdx === idx;
                return (
                  <ComponentRow
                    key={idx}
                    component={c}
                    isEditing={isEditing}
                    onStartEdit={() => {
                      haptic('light');
                      setEditingIdx(idx);
                    }}
                    onStopEdit={() => setEditingIdx(null)}
                    onChange={(patch) => updateComponent(idx, patch)}
                    onRemove={() => removeComponent(idx)}
                  />
                );
              })}

              {/* Кнопка добавить */}
              <button
                type="button"
                onClick={addComponent}
                className="w-full mt-1.5 py-1.5 rounded-md bg-sky-glow/10 border border-sky-glow/30 text-sky-glow text-[11px] font-medium flex items-center justify-center gap-1 hover:bg-sky-glow/15 transition-colors active:scale-[0.98]"
              >
                <Plus size={11} />
                Добавить компонент
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ComponentRow — одна строка компонента
// Аккордеон-стиль: компактная строка → раскрывается inline-редактор
// ════════════════════════════════════════════════════════════════

function ComponentRow({
  component,
  isEditing,
  onStartEdit,
  onStopEdit,
  onChange,
  onRemove,
}: {
  component: MealComponent;
  isEditing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onChange: (patch: Partial<MealComponent>) => void;
  onRemove: () => void;
}) {
  const { haptic } = useTelegram();
  const [aiLoading, setAiLoading] = useState(false);

  // 🆕 v3: режим веса — число или описание
  // Если у компонента есть weight_desc — стартуем в режиме описания
  const ext = component as MealComponentExt;
  const [weightMode, setWeightMode] = useState<'grams' | 'desc'>(
    ext.weight_desc ? 'desc' : 'grams'
  );
  const [weightDesc, setWeightDesc] = useState<string>(ext.weight_desc || '');

  // 🆕 v3: AI пересчёт с поддержкой свободной формы веса
  async function recalcWithAI() {
    const name = (component.name || '').trim();
    if (!name) {
      alert('Введи название компонента');
      return;
    }

    // 🆕 v3: формируем запрос в зависимости от режима веса
    let query: string;
    if (weightMode === 'desc' && weightDesc.trim()) {
      // Свободная форма: "помидор кусочек", "орехи горсть"
      query = `${name} ${weightDesc.trim()}`;
    } else if (weightMode === 'grams' && Number(component.weight_g) > 0) {
      query = `${name} ${component.weight_g}г`;
    } else {
      alert('Введи вес или описание (например "кусочек", "горсть")');
      return;
    }

    setAiLoading(true);
    haptic('medium');

    try {
      console.log('[ComponentsEditor] AI recalc query:', query);
      const res = await analyzeText(query);
      const aiItem: any = res.items?.[0];

      if (!aiItem) {
        alert('AI не смог распознать. Попробуй переформулировать.');
        haptic('error');
        return;
      }

      // 🆕 v3: AI вернул вес в граммах — берём его если у нас было описание
      const aiWeight = Math.max(1, Math.round(Number(aiItem.estimated_weight_g) || 0));
      const newWeight = weightMode === 'desc' ? aiWeight : (component.weight_g || aiWeight);

      onChange({
        weight_g: newWeight,
        calories: Math.max(0, Math.round(Number(aiItem.calories) || 0)),
        protein_g: +(Math.max(0, Number(aiItem.protein_g) || 0)).toFixed(1),
        fat_g: +(Math.max(0, Number(aiItem.fat_g) || 0)).toFixed(1),
        carbs_g: +(Math.max(0, Number(aiItem.carbs_g) || 0)).toFixed(1),
        // 🆕 Сохраняем описание чтобы юзер видел что он вводил
        weight_desc: weightMode === 'desc' ? weightDesc.trim() : undefined,
      } as any);

      haptic('success');
    } catch (e) {
      console.error('[ComponentsEditor] AI recalc failed:', e);
      alert('Не удалось посчитать через AI. Попробуй ещё раз.');
      haptic('error');
    } finally {
      setAiLoading(false);
    }
  }

  // ───────────────────────────────────────────────
  // Компактный вид (закрытая строка аккордеона)
  // ───────────────────────────────────────────────
  if (!isEditing) {
    const weightLabel = ext.weight_desc
      ? ext.weight_desc
      : `${fmtNum(Number(component.weight_g || 0), 0)}г`;

    return (
      <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-bg-elevated/40 rounded-md transition-colors group">
        <button
          type="button"
          onClick={onStartEdit}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          <span className="text-[12px] text-ink truncate flex-1 min-w-0">
            • {component.name || 'Без названия'}
          </span>
          <span className="text-[10px] text-ink-dim shrink-0">
            {weightLabel}
          </span>
          <span className="text-[10px] text-ink-muted shrink-0 w-12 text-right">
            {fmtNum(Number(component.calories || 0), 0)} ккал
          </span>
        </button>
        <button
          type="button"
          onClick={onStartEdit}
          className="text-ink-dim hover:text-emerald-glow shrink-0 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Редактировать"
        >
          <Edit3 size={11} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="text-ink-dim hover:text-danger shrink-0 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Удалить"
        >
          <Trash2 size={11} />
        </button>
      </div>
    );
  }

  // ───────────────────────────────────────────────
  // Развёрнутый редактор (внутри той же строки)
  // ───────────────────────────────────────────────
  const isFreshComponent = !component.name && !component.calories;
  const canRecalcAI = !!(component.name || '').trim() &&
    ((weightMode === 'grams' && Number(component.weight_g) > 0) ||
     (weightMode === 'desc' && weightDesc.trim().length > 0));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-bg-elevated/80 border border-sky-glow/30 rounded-md p-2 space-y-1.5"
    >
      {/* Название + кнопки готово/удалить */}
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          placeholder="Название (Рис, Курица...)"
          value={component.name}
          onChange={(e) => onChange({ name: e.target.value })}
          className="input text-xs py-1 px-2 flex-1 min-w-0"
          maxLength={80}
          autoFocus
        />
        <button
          type="button"
          onClick={onStopEdit}
          className="w-6 h-6 rounded text-emerald-glow hover:bg-emerald-glow/15 flex items-center justify-center transition-colors shrink-0"
          title="Готово"
        >
          <Check size={11} />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="w-6 h-6 rounded text-ink-dim hover:text-danger hover:bg-danger/10 flex items-center justify-center transition-colors shrink-0"
          title="Удалить"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {/* 🆕 v3: Переключатель режима веса */}
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => {
            setWeightMode('grams');
            haptic('light');
          }}
          className={`flex-1 py-1 rounded text-[10px] font-medium transition-colors ${
            weightMode === 'grams'
              ? 'bg-emerald-glow text-bg'
              : 'bg-bg-card text-ink-muted border border-line'
          }`}
        >
          Граммы
        </button>
        <button
          type="button"
          onClick={() => {
            setWeightMode('desc');
            haptic('light');
          }}
          className={`flex-1 py-1 rounded text-[10px] font-medium transition-colors flex items-center justify-center gap-1 ${
            weightMode === 'desc'
              ? 'bg-emerald-glow text-bg'
              : 'bg-bg-card text-ink-muted border border-line'
          }`}
        >
          <Ruler size={9} />
          На глаз
        </button>
      </div>

      {/* Вес: число или описание в зависимости от режима */}
      {weightMode === 'grams' ? (
        <div className="grid grid-cols-2 gap-1">
          <div>
            <label className="text-[9px] text-ink-dim block mb-0.5">Вес, г</label>
            <input
              type="number"
              inputMode="numeric"
              value={component.weight_g || ''}
              onChange={(e) => onChange({ weight_g: Number(e.target.value) || 0 })}
              className="input text-xs py-1 px-2"
              min={0}
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-[9px] text-ink-dim block mb-0.5">Калории</label>
            <input
              type="number"
              inputMode="numeric"
              value={component.calories || ''}
              onChange={(e) => onChange({ calories: Number(e.target.value) || 0 })}
              className="input text-xs py-1 px-2"
              min={0}
              placeholder="0"
            />
          </div>
        </div>
      ) : (
        <div>
          <label className="text-[9px] text-ink-dim block mb-0.5">
            Сколько съел? (опиши свободно)
          </label>
          <input
            type="text"
            value={weightDesc}
            onChange={(e) => setWeightDesc(e.target.value)}
            className="input text-xs py-1 px-2"
            placeholder="кусочек, горсть, со спичечный коробок..."
            maxLength={80}
          />
          <p className="text-[9px] text-ink-dim mt-0.5 italic">
            💡 AI сам поймёт и переведёт в граммы
          </p>
        </div>
      )}

      {/* БЖУ в одну строку — компактно */}
      <div className="grid grid-cols-3 gap-1">
        <div>
          <label className="text-[9px] text-emerald-glow block mb-0.5">Б, г</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={(component as any).protein_g || ''}
            onChange={(e) => onChange({ protein_g: Number(e.target.value) || 0 } as any)}
            className="input text-xs py-1 px-2"
            min={0}
            placeholder="0"
          />
        </div>
        <div>
          <label className="text-[9px] text-warn block mb-0.5">Ж, г</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={(component as any).fat_g || ''}
            onChange={(e) => onChange({ fat_g: Number(e.target.value) || 0 } as any)}
            className="input text-xs py-1 px-2"
            min={0}
            placeholder="0"
          />
        </div>
        <div>
          <label className="text-[9px] text-sky-glow block mb-0.5">У, г</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={(component as any).carbs_g || ''}
            onChange={(e) => onChange({ carbs_g: Number(e.target.value) || 0 } as any)}
            className="input text-xs py-1 px-2"
            min={0}
            placeholder="0"
          />
        </div>
      </div>

      {/* AI пересчёт */}
      <button
        type="button"
        onClick={recalcWithAI}
        disabled={!canRecalcAI || aiLoading}
        className={`w-full py-1.5 rounded text-[11px] font-medium flex items-center justify-center gap-1 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
          isFreshComponent
            ? 'bg-emerald-glow/15 border border-emerald-glow/40 text-emerald-glow hover:bg-emerald-glow/20'
            : 'bg-bg-elevated border border-line text-ink-muted hover:text-emerald-glow hover:border-emerald-glow/30'
        }`}
      >
        {aiLoading ? (
          <>
            <Loader2 size={11} className="animate-spin" />
            AI считает…
          </>
        ) : (
          <>
            <Sparkles size={11} />
            {isFreshComponent ? 'Посчитать через AI' : 'Пересчитать через AI'}
          </>
        )}
      </button>

      {isFreshComponent && (
        <p className="text-[9px] text-ink-dim text-center leading-relaxed">
          💡 Введи название + вес/описание → AI заполнит КБЖУ
        </p>
      )}
    </motion.div>
  );
}
